import React, { useEffect, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  Icon,
  IconButton,
  Text,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import {
  IoDocumentAttachOutline,
  IoInformationCircleOutline,
  IoOpen,
  IoTrash,
} from "react-icons/io5";
import { useTranslation } from "next-i18next";
import PdfViewer from "./PdfViewer";

export type PreviewTab = "document" | "summary";

type Props = {
  invoiceId?: string;
  attachmentUrl: string;
  isPdf: boolean;
  invoiceKind: string;
  shop: string;
  dateIso: string;
  invoiceNumber?: string;
  total: string;
  totalNet: string;
  tax: string;
  currency: string;
  canEditAttachment: boolean;
  onRemoveAttachment: () => void | Promise<void>;
};

export default function InvoiceAttachmentPreviewPanel({
  invoiceId,
  attachmentUrl,
  isPdf,
  invoiceKind,
  shop,
  dateIso,
  invoiceNumber,
  total,
  totalNet,
  tax,
  currency,
  canEditAttachment,
  onRemoveAttachment,
}: Props) {
  const { t } = useTranslation("common");
  const [tab, setTab] = useState<PreviewTab>("document");

  useEffect(() => {
    setTab("document");
  }, [invoiceId, attachmentUrl]);

  const formattedDate = (() => {
    try {
      return new Date(dateIso).toLocaleDateString("de-DE");
    } catch {
      return dateIso;
    }
  })();

  return (
    <Box
      className="pdf-viewer"
      bg="white"
      borderRadius="lg"
      boxShadow="lg"
      borderWidth="1px"
      borderColor="gray.200"
    >
      <Flex direction="column" h="100%" minH={0}>
        <Box px={3} pt={3} pb={2} flexShrink={0}>
          <Box
            bg="gray.100"
            p={1}
            borderRadius="lg"
            display="inline-flex"
            flexWrap="wrap"
            gap={1}
          >
            <Button
              size="sm"
              h="32px"
              px={3}
              borderRadius="md"
              fontWeight={tab === "document" ? "600" : "500"}
              bg={tab === "document" ? "white" : "transparent"}
              boxShadow={tab === "document" ? "sm" : "none"}
              color={tab === "document" ? "gray.800" : "gray.600"}
              _hover={
                tab !== "document"
                  ? { bg: "white", boxShadow: "sm", color: "gray.800" }
                  : {}
              }
              onClick={() => setTab("document")}
              leftIcon={
                <Icon
                  as={IoDocumentAttachOutline}
                  boxSize={3.5}
                  color={tab === "document" ? "rekening.600" : "gray.500"}
                />
              }
              transition="all 0.15s ease"
            >
              {t("invoiceDetails.previewPanelTabDocument")}
            </Button>
            <Button
              size="sm"
              h="32px"
              px={3}
              borderRadius="md"
              fontWeight={tab === "summary" ? "600" : "500"}
              bg={tab === "summary" ? "white" : "transparent"}
              boxShadow={tab === "summary" ? "sm" : "none"}
              color={tab === "summary" ? "gray.800" : "gray.600"}
              _hover={
                tab !== "summary"
                  ? { bg: "white", boxShadow: "sm", color: "gray.800" }
                  : {}
              }
              onClick={() => setTab("summary")}
              leftIcon={
                <Icon
                  as={IoInformationCircleOutline}
                  boxSize={3.5}
                  color={tab === "summary" ? "rekening.600" : "gray.500"}
                />
              }
              transition="all 0.15s ease"
            >
              {t("invoiceDetails.previewPanelTabSummary")}
            </Button>
          </Box>
        </Box>

        <Box flex="1" minH={0} display="flex" flexDirection="column" overflow="hidden">
          {tab === "document" ? (
            <>
              <HStack
                px={3}
                py={2}
                borderBottomWidth="1px"
                borderColor="gray.200"
                justify="flex-end"
                flexShrink={0}
                spacing={2}
              >
                <Tooltip label={t("invoiceDetails.preview")}>
                  <IconButton
                    aria-label={t("invoiceDetails.preview")}
                    icon={<IoOpen />}
                    size="sm"
                    variant="outline"
                    as="a"
                    href={attachmentUrl}
                    target="_blank"
                    rel="noreferrer"
                  />
                </Tooltip>
                {canEditAttachment && (
                  <Tooltip label={t("invoiceDetails.delete")}>
                    <IconButton
                      aria-label={t("invoiceDetails.delete")}
                      icon={<IoTrash />}
                      size="sm"
                      variant="outline"
                      colorScheme="red"
                      onClick={() => void onRemoveAttachment()}
                    />
                  </Tooltip>
                )}
              </HStack>
              <Box
                flex="1"
                minH={0}
                position="relative"
                overflow="hidden"
                display="flex"
                flexDirection="column"
              >
                {isPdf ? (
                  <PdfViewer url={attachmentUrl} embedded />
                ) : (
                  <Box
                    h="100%"
                    w="100%"
                    bg="gray.50"
                    backgroundImage={`url(${attachmentUrl})`}
                    backgroundSize="contain"
                    backgroundRepeat="no-repeat"
                    backgroundPosition="center"
                  />
                )}
              </Box>
            </>
          ) : (
            <Box px={4} py={3} overflowY="auto" flex="1" minH={0}>
              <VStack align="stretch" spacing={3}>
                <HStack>
                  <Text fontSize="sm" fontWeight="semibold" color="gray.800" noOfLines={2}>
                    {shop || "—"}
                  </Text>
                  <Badge colorScheme="gray" variant="subtle" flexShrink={0}>
                    {invoiceKind === "travel-expense"
                      ? t("invoiceDetails.travelExpense")
                      : t("invoiceDetails.invoice")}
                  </Badge>
                </HStack>
                <Text fontSize="sm" color="gray.600">
                  {formattedDate}
                </Text>
                {invoiceNumber ? (
                  <Text fontSize="sm" color="gray.600">
                    {t("invoiceDetails.invoiceNumber")}: {invoiceNumber}
                  </Text>
                ) : null}
                <Divider />
                <Box>
                  <Text fontSize="xs" color="gray.500" fontWeight="600" textTransform="uppercase">
                    {t("invoiceDetails.sectionAmounts")}
                  </Text>
                  <Text fontSize="lg" fontWeight="bold" color="gray.800" mt={1}>
                    {total} {currency}
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    {totalNet} ({tax}) {currency}
                  </Text>
                </Box>
              </VStack>
            </Box>
          )}
        </Box>
      </Flex>
    </Box>
  );
}
